import { useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import styled from '@mui/material/styles/styled';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ProjectManagerForm from './ProjectManagerForm';
import { sentenceCase } from '../../../utils/utils';
import Text from '../../elements/text/Text';
import Title from '../../elements/title/Title';
import Modal from '../../sections/modal/Modal';
import NoContent from '../../sections/no-content/NoContent';

const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(2),
  float: 'right',
}));

const ProjectManager = ({ onSubmit, manager }) => {
  const { t } = useTranslation();

  const [addModal, setAddModal] = useState(false);
  const toggleAddModal = () => setAddModal(!addModal);

  const handleSubmit = async (data) => {
    await onSubmit(data);
    toggleAddModal();
  };

  const projectManagerFields = [
    {
      label: t('name', { ns: 'fields' }),
      val: 'name',
    },
    {
      label: t('jobTitle', { ns: 'fields' }),
      val: 'title',
    },
    {
      label: t('companyName', { ns: 'fields' }),
      val: 'company',
    },
    {
      label: t('address', { ns: 'fields' }),
      val: 'address',
    },
    {
      label: t('phoneNumber', { ns: 'fields' }),
      val: 'phone',
    },
    {
      label: t('email', { ns: 'fields' }),
      val: 'email',
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title subTitle text={t('projectManager', { ns: 'sectionTitles' })} />
        {manager && (
          <StyledButton
            startIcon={<EditIcon />}
            variant="outlined"
            onClick={toggleAddModal}
            aria-label="edit"
          >
            {t('updateProjectManager', { ns: 'buttons' })}
          </StyledButton>
        )}
      </Box>

      {manager ? (
        <TableContainer>
          <Table size="small">
            <TableBody>
              {projectManagerFields.map((pmField) => (
                <TableRow key={pmField.label}>
                  <TableCell>
                    <Text body text={pmField.label} />
                  </TableCell>
                  <TableCell>{manager?.[pmField.val]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <NoContent
          title={sentenceCase(
            t('empty', {
              ns: 'descriptions',
              text: t('pm.info', { ns: 'project' }),
            })
          )}
          button={
            <Button
              onClick={toggleAddModal}
              aria-label={'add a project manager'}
            >
              {t('addProjectManager', { ns: 'buttons' })}
            </Button>
          }
        />
      )}
      <Modal
        openModal={addModal}
        title={t('pm.info', { ns: 'project' })}
        handleClose={toggleAddModal}
      >
        <ProjectManagerForm
          onSubmit={handleSubmit}
          onClose={toggleAddModal}
          data={manager}
        />
      </Modal>
    </Box>
  );
};

ProjectManager.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  manager: PropTypes.object,
};

export default ProjectManager;
